import { PANEL_EDGE_OFFSET, applySide, withSafeArea } from './chrome.js';
import { dismissInboxReply, inboxReplyDismissed, readInboxPeek, writeInboxPeek } from './visit-marks.js';
import { inboxUrl, origin, pageLanguage } from './snippet.js';
import { xMarkIconSvg } from './leadwin-agent.js';

/**
 * The launcher's unread replies, the way a messenger announces them: a red dot on the closed launcher and,
 * above it, one card with the newest waiting reply. The start of what was written in a bubble, and under it,
 * left-aligned, who wrote it (their photo or initials and their name; the company's logo and name where the
 * account owner answered without a team profile). A click opens exactly that conversation; the close control puts the card away
 * for this reply, while the dot stays until the visitor has read it.
 *
 * Only a team person's reply counts, never the agent's: the agent answers within the second the visitor
 * asked, and a reply they watched arrive is not waiting for anybody. The server decides that, here and in
 * the widget's own messages tab alike.
 *
 * Two ways the news arrives:
 * - On page load, before any frame exists. The frame of an earlier visit left a peek token in this page's
 *   storage, and the loader asks the server with it (see readInboxPeek).
 * - Live, from a frame that is already standing behind a closed panel: it posts 'feedbax:widget:inbox'
 *   whenever a reply lands or the visitor reads one, and that always wins over the page-load answer.
 *
 * The card takes the bubbles' corner and their slot: one interruption at a time, and a person's reply
 * beats any offer the widget makes on its own. Every text is set through textContent; it is the provider
 * team's own writing, but it travels through a network payload into a page we do not own.
 * @param {object} ctx - The instance context (see init)
 * @returns {object} The handles the rest of the instance needs
 */
export function createInboxSurface(ctx) {
    const { state, options, button, trust, proactive, setOpen, effectiveTheme } = ctx;

    /* The newest waiting reply, or null; and the card that shows it, or null. On state because the corner's
     * other surfaces measure and respect the card (see proactiveSlotFree, positionTrustCards). */
    state.inboxUnread = null;
    state.unreadCard = null;

    /* Once the frame has spoken it knows better than a page-load answer that may still be on its way. */
    let frameSpoke = false;
    let cardResize = null;

    /**
     * Asks the server with the peek token from an earlier visit. Fire and forget: a missing token, a slow
     * answer or a failed one all mean no card, which is what the page looked like before this existed.
     */
    function loadOnPageLoad() {
        const peek = readInboxPeek();

        if (!peek || typeof fetch !== 'function') {
            return;
        }

        // The page's language travels like on the config: a reply of pictures only is announced as
        // "📷 Bild" in it.
        const url = inboxUrl + '?t=' + encodeURIComponent(peek)
            + (pageLanguage ? '&lang=' + encodeURIComponent(pageLanguage) : '');

        fetch(url, { credentials: 'omit' })
            .then(function (response) {
                return response && response.status === 200 ? response.json() : null;
            })
            .then(function (data) {
                if (!frameSpoke && data) {
                    applyUnread(data);
                }
            })
            .catch(function () { /* no card; the frame brings it on the next open */ });
    }

    /**
     * What the frame posted: the peek token to keep for the next page load, and the newest waiting reply.
     * @param {{peek: unknown, unread: unknown}} data - The frame's message
     */
    function applyFrameInbox(data) {
        frameSpoke = true;

        // Only a token replaces a token. A frame without conversations sends none, and keeping the old one
        // costs nothing: the server answers it with an empty 204.
        if (typeof data.peek === 'string' && data.peek) {
            writeInboxPeek(data.peek);
        }

        applyUnread(data.unread);
    }

    /**
     * Validates a reply from either source and repaints dot and card.
     * @param {unknown} raw - The reply as the server or the frame sent it, or null for "nothing waits"
     */
    function applyUnread(raw) {
        state.inboxUnread = normalizeUnread(raw);
        refresh();
    }

    /**
     * Brings dot and card in line with the reply and the panel. Called after every change of either: the
     * open panel is where the visitor reads, so nothing stands on the launcher while it is open.
     *
     * There is one red dot, and it sits on whatever announces the reply: on the card's bubble while the
     * card stands, on the launcher whenever it does not (closed by the visitor, or a reply without text).
     */
    function refresh() {
        const unread = state.inboxUnread;
        const closed = !state.panelOpen;

        syncCard(unread, closed);

        button.classList.toggle('lw-launcher-unread', Boolean(unread) && closed && !state.unreadCard);
    }

    /**
     * Shows, keeps or removes the card for the reply.
     * @param {object|null} unread
     * @param {boolean} closed - Whether the panel is closed
     */
    function syncCard(unread, closed) {
        // A reply of pictures or files arrives as "📷 Bild" / "📎 Datei" (see WidgetInboxPreview); only a
        // reply with nothing at all to quote gets the dot alone.
        const wanted = unread && closed && unread.preview && !inboxReplyDismissed(markOf(unread));

        if (!wanted) {
            removeCard();
            return;
        }

        if (state.unreadCard && state.unreadCard.dataset.mark === markOf(unread)) {
            return; // Already showing this very reply.
        }

        removeCard();

        // The slot is the card's now: a bubble or a campaign still on screen goes, and the timers that would
        // bring the next one check the slot before they show anything.
        if (state.nudgeStack) {
            proactive.retireNudges('a team reply took the slot');
        }

        state.unreadCard = createCard(unread);
        trust.positionTrustCards();
    }

    /** Takes the card down, if one stands; the dot is refresh's business. */
    function removeCard() {
        if (cardResize) {
            cardResize.disconnect();
            cardResize = null;
        }

        if (state.unreadCard) {
            state.unreadCard.remove();
            state.unreadCard = null;
            trust.positionTrustCards();
        }
    }

    /**
     * Builds the card in the bubbles' corner and look: the same stack class carries the theme tokens, so a
     * dark widget gets a dark card without a second stylesheet.
     * @param {{conversationId: string, name: string, photoUrl: string, preview: string, at: string}} unread
     * @returns {HTMLElement}
     */
    function createCard(unread) {
        const stack = document.createElement('div');
        stack.id = 'leadwin-agent-unread';
        stack.className = 'lw-nudges lw-unread';
        stack.dataset.mark = markOf(unread);
        stack.setAttribute('data-side', options.side);
        stack.dataset.theme = effectiveTheme();

        // Placement as the bubbles have it (createNudgeStack): 12px above the 48px launcher and its safe area.
        stack.style.position = 'fixed';
        stack.style.zIndex = '2147483646';
        stack.style.bottom = withSafeArea('78px', 'bottom');
        stack.style.alignItems = options.side === 'left' ? 'flex-start' : 'flex-end';
        applySide(stack, options.side, PANEL_EDGE_OFFSET);
        stack.style.maxWidth = 'min(300px, calc(100vw - 36px))';

        // Bubble and sender hang on the launcher's side, so a short message sits right above the launcher
        // however long the name under it is. The whole card opens the conversation; the bubble is the
        // control a keyboard reaches, the sender line under it is just more surface to hit.
        const card = document.createElement('div');
        card.className = 'lw-unread-card';
        card.addEventListener('click', function () {
            openConversation(unread.conversationId);
        });

        // The close control rides on the bubble itself, not on the card: with the card aligned to the
        // launcher, the card's corner can lie far from a short bubble.
        const bubbleWrap = document.createElement('div');
        bubbleWrap.className = 'lw-unread-bubble-wrap';

        // Bubble and text are two elements on purpose: the two-line clamp cuts at the padding edge, so with
        // the padding on the clamped element the third line showed half in the bubble's bottom padding.
        const bubble = document.createElement('button');
        bubble.type = 'button';
        bubble.className = 'lw-unread-bubble';
        const text = document.createElement('span');
        text.className = 'lw-unread-text';
        text.textContent = unread.preview;
        bubble.appendChild(text);
        bubbleWrap.appendChild(bubble);

        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'lw-nudge-close';
        close.setAttribute('aria-label', options.nudgeCloseLabel);
        close.innerHTML = xMarkIconSvg;
        close.addEventListener('click', function (event) {
            event.stopPropagation(); // A close is not an open.
            dismissInboxReply(markOf(unread));
            refresh(); // The card goes, and its dot moves over to the launcher.
        });
        bubbleWrap.appendChild(close);

        card.appendChild(bubbleWrap);

        if (unread.name) {
            card.appendChild(createHead(unread));
        }

        stack.appendChild(card);
        document.body.appendChild(stack);

        // The card inherits the host page's font, so a face swapping in later rewraps it; the trust cards
        // above have to follow its height, exactly as they follow the bubbles'.
        if (typeof ResizeObserver === 'function') {
            cardResize = new ResizeObserver(trust.positionTrustCards);
            cardResize.observe(stack);
        }

        return stack;
    }

    /**
     * The sender line: their photo where they uploaded one, otherwise their initials, then their name.
     * @param {{name: string, photoUrl: string}} unread
     * @returns {HTMLElement}
     */
    function createHead(unread) {
        const head = document.createElement('span');
        head.className = 'lw-unread-head';

        if (unread.photoUrl) {
            const photo = document.createElement('img');
            photo.className = 'lw-unread-avatar';
            photo.alt = '';
            photo.src = unread.photoUrl;
            head.appendChild(photo);
        } else {
            const initials = document.createElement('span');
            initials.className = 'lw-unread-avatar lw-unread-initials';
            initials.setAttribute('aria-hidden', 'true');
            // One letter for a company, as the chat header draws it; two for a person.
            initials.textContent = unread.company ? initialsOf(unread.name).slice(0, 1) : initialsOf(unread.name);
            head.appendChild(initials);
        }

        const name = document.createElement('span');
        name.className = 'lw-unread-name';
        name.textContent = unread.name;
        head.appendChild(name);

        return head;
    }

    /**
     * Opens the panel on the card's conversation. A standing frame gets the id at once; a frame still to be
     * mounted gets it on its subscription handshake (see the 'subscribed' replay in leadwin-agent.js).
     * @param {string} conversationId
     */
    function openConversation(conversationId) {
        removeCard(); // setOpen below refreshes the dot: the open panel carries none.

        if (state.frameSubscribed && state.iframe && state.iframe.contentWindow) {
            postConversationToFrame(conversationId);
        } else {
            state.pendingConversationId = conversationId;
        }

        setOpen(true);
    }

    /** @param {string} conversationId */
    function postConversationToFrame(conversationId) {
        if (state.iframe && state.iframe.contentWindow) {
            state.iframe.contentWindow.postMessage(
                { type: 'feedbax:widget:openconversation', id: conversationId }, origin);
        }
    }

    /** Repaints the card for a changed look, as the bubbles are. */
    function applyTheme(theme) {
        if (state.unreadCard) {
            state.unreadCard.dataset.theme = theme;
        }
    }

    /** Moves the card to the launcher's new side after a live save, as the bubbles are. */
    function applyLiveSide(side) {
        if (state.unreadCard) {
            applySide(state.unreadCard, side, PANEL_EDGE_OFFSET);
            state.unreadCard.setAttribute('data-side', side);
            state.unreadCard.style.alignItems = side === 'left' ? 'flex-start' : 'flex-end';
        }
    }

    return { loadOnPageLoad, applyFrameInbox, refresh, postConversationToFrame, applyTheme, applyLiveSide };
}

/**
 * Takes a reply apart field by field. Anything that is not what it claims to be is dropped, and a reply
 * without a conversation to open is no reply at all.
 * @param {unknown} raw
 * @returns {{conversationId: string, name: string, company: boolean, photoUrl: string, preview: string, at: string}|null}
 */
export function normalizeUnread(raw) {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const conversationId = typeof raw.conversationId === 'string' ? raw.conversationId.trim() : '';

    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(conversationId)) {
        return null;
    }

    return {
        conversationId: conversationId,
        name: typeof raw.name === 'string' ? raw.name.trim().slice(0, 80) : '',
        company: raw.company === true,
        photoUrl: safePhotoUrl(raw.photoUrl),
        preview: typeof raw.preview === 'string' ? raw.preview.trim().slice(0, 300) : '',
        at: typeof raw.at === 'string' ? raw.at.slice(0, 40) : ''
    };
}

/**
 * A team photo is usually a path on the widget's own host; it is resolved against that origin, and only
 * http(s) is ever put into an img on somebody else's page.
 * @param {unknown} value
 * @returns {string}
 */
function safePhotoUrl(value) {
    if (typeof value !== 'string' || !value.trim()) {
        return '';
    }

    try {
        const url = new URL(value.trim(), origin);
        return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
    } catch {
        return '';
    }
}

/** Which reply a dismissal was about: the conversation and the moment of its newest waiting message. */
function markOf(unread) {
    return unread.conversationId + '|' + unread.at;
}

/**
 * Up to two initials, as the widget's own avatars draw them ("Elisa Mützel" is "EM").
 * @param {string} name
 * @returns {string}
 */
function initialsOf(name) {
    const parts = name.split(/\s+/).filter(Boolean);
    const letters = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : (parts[0] || '').slice(0, 1);
    return letters.toUpperCase();
}
